/** 场景生成页两个业务标签。URL `?tab=` 只认这两个值，其它都回到规划。 */
export type SceneGenerationTab = 'pnc' | 'perception';

export function parseSceneGenerationTab(raw: string | null | undefined): SceneGenerationTab {
  return raw === 'perception' ? 'perception' : 'pnc';
}

/** 感知入库提交体。区域、模式、业务域都是固定值，不从页面控件来。 */
export function perceptionSubmitBody(
  caseIds: string,
  sceneSetNodeId?: number,
  securityLevel?: number,
) {
  return {
    caseIds,
    domain: 'perception' as const,
    region: 'cn' as const,
    genMode: 'generate_and_qc_check' as const,
    sceneSetNodeId,
    securityLevel,
  };
}

export function sceneJobListParams(taskTypeName: 'SCENE' | 'SCENE_PE') {
  return { taskTypeName };
}

export interface TreeNodeLike {
  id: number;
  children?: TreeNodeLike[];
}

/** 只保留指定根节点及其子树。根 id 为空或树上找不到时返回空数组。 */
export function findSceneSubtree<T extends TreeNodeLike>(
  nodes: T[],
  rootId: number | null,
): T[] {
  if (rootId == null) return [];
  const walk = (list: T[]): T | undefined => {
    for (const node of list) {
      if (node.id === rootId) return node;
      const hit = node.children?.length ? walk(node.children as T[]) : undefined;
      if (hit) return hit;
    }
    return undefined;
  };
  const root = walk(nodes);
  return root ? [root] : [];
}

/** 感知卡片关心的批次状态字段（与 ``CreateSceneFromCaseFlowState`` 同名，避免引入 api 模块）。 */
export interface PerceptionBatchStateLike {
  status?: string | null;
  interrupted?: boolean | null;
  jobId?: number | null;
  stage?: string | null;
  processedCount?: number | null;
  totalCount?: number | null;
}

export interface PerceptionBatchView {
  running: boolean;
  /** 状态还挂着 running、但后台线程已消失（服务重启 / 执行异常），可「继续」 */
  interrupted: boolean;
  /** 台账 Job id；有它才能取消 / 继续 */
  jobId: number | null;
  /** 还没处理的条数（中断提示用） */
  remaining: number;
  /** 运行中的一行进度文字；不在运行中为空串 */
  progressText: string;
}

/** 由批次状态推出感知卡片的展示：进度、是否中断、能不能取消 / 继续。 */
export function perceptionBatchView(state: PerceptionBatchStateLike | null | undefined): PerceptionBatchView {
  const running = state?.status === 'running';
  const interrupted = running && !!state?.interrupted;
  const processed = state?.processedCount ?? null;
  const total = state?.totalCount ?? null;
  const remaining = Math.max(0, (total ?? 0) - (processed ?? 0));
  let progressText = '';
  if (running) {
    if (interrupted) progressText = `已中断 · 可继续（剩余 ${remaining} 条）`;
    else if (state?.stage === 'creating') progressText = '正在创建批次任务…';
    else if (state?.stage === 'dispatching') progressText = '正在调度构建…';
    else if (processed != null && total != null) progressText = `正在拉取并创建 ${processed} / ${total}`;
    else progressText = '入库进行中…';
  }
  return { running, interrupted, jobId: state?.jobId ?? null, remaining, progressText };
}
